import { useState, useEffect } from "react";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";

import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import { getUsers, postUser, putUser } from "../../../api/users";

import { areaColor } from "../../../config/enum";

import PropTypes from "prop-types";

import { setAllUsers } from "../../../redux/reducers/usersReducer";
import { useDispatch } from "react-redux";

import UserInfos from "./UserInfos";
import UserRole from "./UserRole";
import UserContact from "./UserContact";

export default function CreateEditUser({
  users,
  fetchUsersWithFilters,
  user = null,
  edit = false,
}) {
  const [open, setOpen] = useState(false);

  const [userId, setUserId] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState(null);
  const [userAreas, setUserAreas] = useState([]);
  const [superior, setSuperior] = useState(null);
  const [color, setColor] = useState("#0082D8");

  const dispatch = useDispatch();

  useEffect(() => {
    if (edit && user) {
      setUserId(user.user_id);
      setUsername(user.username);
      setPassword(user.password);
      setFirstName(user.first_name);
      setLastName(user.last_name);
      setEmail(user.email);
      setPhoneNumber(user.phone_number);
      setRole(user.role);
      setUserAreas(typeof user.user_areas === 'string' ? JSON.parse(user.user_areas) : (user.user_areas || []));
      setSuperior(
        user.superior !== null
          ? {
              label: user.superior_name,
              id: user.superior,
            }
          : null
      );
      setColor(user.color || "#0082D8"); // Set color from user data
    }
  }, [edit, user]);

  async function createUserAPI() {
    const user = {
      username: username,
      password: password,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone_number: phoneNumber,
      role: role ? role : null,
      user_areas: userAreas.length > 0 ? userAreas : [],
      superior: superior ? superior.id : null,
      color: role === "Contremaître" ? color : null, // Include color if Contremaître
    };
    await postUser(user);
  }

  async function updateUserAPI() {
    const user = {
      username: username,
      password: password,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone_number: phoneNumber,
      role: role ? role : null,
      user_areas: userAreas.length > 0 ? userAreas : [],
      superior: superior ? superior.id : null,
      color: role === "Contremaître" ? color : null,
    };
    await putUser(user, userId);
  }

  function resetData() {
    setUserId("");
    setUsername("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhoneNumber("");
    setRole(null);
    setUserAreas([]);
    setSuperior(null);
    setColor("#0082D8");
  }

  async function fetchUsers() {
    const response = await getUsers();
    const formatedUsers = await response.data.map((user) => ({
      label: user.name,
      id: user.user_id,
    }));
    dispatch(setAllUsers(formatedUsers));
  }

  async function submit() {
    if (edit) {
      await updateUserAPI();
      await fetchUsersWithFilters();
      await fetchUsers();
    } else {
      await createUserAPI();
      await fetchUsersWithFilters();
      await fetchUsers();
      resetData();
    }
    setStep(0);
    setOpen(false);
  }

  async function cancel() {
    if (!edit) {
      resetData();
    }
    setStep(0);
    setOpen(false);
  }

  const [step, setStep] = useState(0);
  const steps = [
    "Informations de l'utilisateur",
    "Coordonnées de l'utilisateur",
    "Rôle de l'utilisateur",
  ];
  const isLastStep = step === steps.length - 1;

  const next = () => setStep(step + 1);
  const previous = () => setStep(step - 1);

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <UserInfos
            username={username}
            setUsername={setUsername}
            password={password}
            setPassword={setPassword}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            email={email}
            setEmail={setEmail}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            userAreas={userAreas}
            setUserAreas={setUserAreas}
            edit={edit}
          />
        );
      case 1:
        return (
          <UserContact
            email={email}
            setEmail={setEmail}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            edit={edit}
          />
        );
      case 2:
        return (
          <UserRole
            role={role}
            setRole={setRole}
            superior={superior}
            setSuperior={setSuperior}
            color={color}
            setColor={setColor}
            edit={edit}
          />
        );
      default:
        return null;
    }
  }

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      {edit ? (
        <MDButton
          variant="contained"
          size="small"
          onClick={() => setOpen(true)}
          color={darkMode ? "white" : "dark"}
          sx={{
            width: 170,
            padding: "6px 0 !important",
            height: 40,
            color: !darkMode ? "white" : "#1A2035",
            boxShadow:
              darkMode &&
              "0rem 0.1875rem 0.1875rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.1875rem 0.0625rem -0.125rem rgba(255, 255, 255, 0.2), 0rem 0.0625rem 0.3125rem 0rem rgba(255, 255, 255, 0.15)",
            "&:hover": {
              boxShadow:
                darkMode &&
                "0rem 0.875rem 1.625rem -0.75rem rgba(255, 255, 255, 0.4), 0rem 0.25rem 1.4375rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          Gérer l'utilisateur
        </MDButton>
      ) : (
        <MDButton
          variant="contained"
          sx={{
            height: 53.13,
            width: "100%",
            color: !darkMode ? "white" : "#1A2035",
            boxShadow:
              darkMode &&
              "0rem 0.1875rem 0.1875rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.1875rem 0.0625rem -0.125rem rgba(255, 255, 255, 0.2), 0rem 0.0625rem 0.3125rem 0rem rgba(255, 255, 255, 0.15)",
            "&:hover": {
              boxShadow:
                darkMode &&
                "0rem 0.875rem 1.625rem -0.75rem rgba(255, 255, 255, 0.4), 0rem 0.25rem 1.4375rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(255, 255, 255, 0.2)",
            },
          }}
          color={darkMode ? "white" : "dark"}
          size="large"
          onClick={() => setOpen(true)}
        >
          Ajouter un utilisteur
        </MDButton>
      )}
      <MDModal open={open} onClose={() => setOpen(false)}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={-12}
                  mb={2}
                  mx={2}
                  sx={{
                    background:
                      user && user.user_areas && user.user_areas.length > 0
                        ? areaColor[user.user_areas[0]]
                        : !darkMode
                        ? "linear-gradient(195deg, #364c70, #273956)"
                        : "white",
                    boxShadow: `0 1px 6px 1px ${
                      user && user.user_areas && user.user_areas.length > 0
                        ? areaColor[user.user_areas[0]]
                        : !darkMode
                        ? "#344767"
                        : "white"
                    }`,
                  }}
                  variant="gradient"
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={1}>
                    <MDBox>
                      {edit ? (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={
                            darkMode && !edit
                              ? "inherit"
                              : darkMode && edit && user && (!user.user_areas || user.user_areas.length === 0)
                              ? "inherit"
                              : "white"
                          }
                        >
                          Modification des données de l'utilisateur
                        </MDTypography>
                      ) : (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={
                            darkMode && !edit
                              ? "inherit"
                              : darkMode && edit && user && (!user.user_areas || user.user_areas.length === 0)
                              ? "inherit"
                              : "white"
                          }
                        >
                          Ajout d'un nouvel utilisateur
                        </MDTypography>
                      )}
                    </MDBox>
                    <MDTypography
                      variant="h5"
                      fontWeight="regular"
                      color={
                        darkMode && !edit
                          ? "inherit"
                          : darkMode && edit && user && (!user.user_areas || user.user_areas.length === 0)
                          ? "inherit"
                          : "white"
                      }
                    >
                      Veuillez entrer les informations relatives au nouveau fournisseur
                    </MDTypography>
                  </MDBox>
                  <Stepper
                    sx={{ boxShadow: "none", backgroundImage: "none" }}
                    activeStep={step}
                    alternativeLabel
                  >
                    {steps.map((label) => (
                      <Step
                        sx={{
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-active":
                            {
                              color:
                                darkMode && !edit
                                  ? "#344767 !important"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767 !important",
                            },
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-completed":
                            {
                              color:
                                darkMode && !edit
                                  ? "#344767 !important"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767 !important",
                            },
                        }}
                        key={label}
                      >
                        <StepLabel
                          sx={{
                            "& .MuiStepLabel-label.Mui-active": {
                              color:
                                darkMode && !edit
                                  ? "#344767 !important"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767 !important",
                            },
                            "& .MuiStepLabel-label.Mui-completed": {
                              color:
                                darkMode && !edit
                                  ? "#344767 !important"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767 !important",
                            },
                            "& .MuiStepIcon-root.Mui-active": {
                              background:
                                darkMode && !edit
                                  ? "#344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767",
                              fill:
                                darkMode && !edit
                                  ? "#344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767",
                              stroke:
                                darkMode && !edit
                                  ? "#344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767",
                              borderColor:
                                darkMode && !edit
                                  ? "#344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767",
                              boxShadow:
                                darkMode && !edit
                                  ? "0rem 0rem 0rem 0.125rem #344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "0rem 0rem 0rem 0.125rem #344767",
                            },
                            "& .MuiStepIcon-root.Mui-completed": {
                              background:
                                darkMode && !edit
                                  ? "#344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767",
                              fill:
                                darkMode && !edit
                                  ? "#344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767",
                              stroke:
                                darkMode && !edit
                                  ? "#344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767",
                              borderColor:
                                darkMode && !edit
                                  ? "#344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "#344767",
                              boxShadow:
                                darkMode && !edit
                                  ? "0rem 0rem 0rem 0.125rem #344767"
                                  : darkMode &&
                                    edit &&
                                    user &&
                                    (!user.user_areas || user.user_areas.length === 0) &&
                                    "0rem 0rem 0rem 0.125rem #344767",
                            },
                          }}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    {getStepContent(step)}
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      {step === 0 ? (
                        <MDBox />
                      ) : (
                        <MDButton variant="gradient" color="warning" onClick={previous}>
                          Précédent
                        </MDButton>
                      )}
                      <MDBox textAlign="right">
                        <Grid container spacing={1.5}>
                          <Grid item>
                            <MDButton variant="gradient" color="warning" onClick={cancel}>
                              Annuler
                            </MDButton>
                          </Grid>
                          <Grid item>
                            <MDButton
                              variant="gradient"
                              color="success"
                              onClick={isLastStep ? submit : next}
                            >
                              {isLastStep ? "Envoyer" : "Suivant"}
                            </MDButton>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}
CreateEditUser.propTypes = {
  edit: PropTypes.bool.isRequired,
};